import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Pencil, UserPlus } from 'lucide-react'
import { DeskTable, type Column } from '@/components/common/DeskTable'
import ShortId from '@/components/common/ShortId'
import StatusBadge from '@/components/common/StatusBadge'
import FilterBarApply from '@/components/common/FilterBarApply'
import FilterBarForm from '@/components/common/FilterBarForm'
import NoAccess from '@/components/common/NoAccess'
import PageFrame from '@/components/common/PageFrame'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { GlassSelect } from '@/components/ui/glass-select'
import { Input } from '@/components/ui/input'
import { accessOf, canManageUsers } from '@/lib/admin-access'
import {
  ApiError,
  backofficeApi,
  type BackofficeUser,
} from '@/lib/api-client'
import { roleLabel } from '@/lib/labels'
import { FILTER_BAR } from '@/lib/ui-shell'
import { useAuthStore } from '@/store/auth'

const ROLES = ['admin', 'superadmin', 'director', 'dealing_settlement', 'finance'] as const

export function ManageUsersPage() {
  const actor = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const [draftSearch, setDraftSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>('dealing_settlement')
  const [editUser, setEditUser] = useState<BackofficeUser | null>(null)
  const [editRole, setEditRole] = useState('')
  const [editEnabled, setEditEnabled] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null)
  const dialogOpen = createOpen || editUser !== null

  const usersQuery = useQuery({
    queryKey: ['bo-users'],
    queryFn: () => backofficeApi.listUsers(),
  })

  const createMut = useMutation({
    mutationFn: () => backofficeApi.createUser({ email: email.trim(), role }),
    onSuccess: (created) => {
      setRevealedPassword(created.temporary_password)
      setCreateOpen(false)
      setEmail('')
      void qc.invalidateQueries({ queryKey: ['bo-users'] })
    },
  })

  const updateMut = useMutation({
    mutationFn: () =>
      backofficeApi.updateUser(editUser!.username, {
        role: editRole,
        enabled: editEnabled,
      }),
    onSuccess: () => {
      setEditUser(null)
      void qc.invalidateQueries({ queryKey: ['bo-users'] })
    },
  })

  const resetMut = useMutation({
    mutationFn: (username: string) => backofficeApi.resetUserPassword(username),
    onSuccess: (res) => {
      setRevealedPassword(res.temporary_password)
      void qc.invalidateQueries({ queryKey: ['bo-users'] })
    },
  })

  const filtered = useMemo(() => {
    const q = appliedSearch.trim().toLowerCase()
    const items = usersQuery.data?.items ?? []
    if (!q) return items
    return items.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q),
    )
  }, [appliedSearch, usersQuery.data?.items])
  const userColumns: Column<BackofficeUser>[] = [
    { key: 'email', header: 'Email', sticky: true, mobile: 'title', sortValue: (u) => u.email || u.username, cell: (u) => u.email || u.username },
    { key: 'role', header: 'Role', mobile: 'subtitle', sortValue: (u) => u.role, cell: (u) => roleLabel(u.role) },
    {
      key: 'status',
      header: 'Status',
      mobile: 'status',
      sortValue: (u) => (!u.enabled ? 'inactive' : u.status),
      cell: (u) => <StatusBadge status={!u.enabled ? 'inactive' : u.status === 'CONFIRMED' ? 'active' : u.status} />,
    },
    { key: 'username', header: 'User ID', sortValue: (u) => u.username, cell: (u) => <ShortId value={u.username} /> },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'actions',
      cell: (user) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openEdit(user)}>
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={resetMut.isPending}
            onClick={() => {
              setError(null)
              resetMut.mutate(user.username, { onError: (e) => setError(errMessage(e)) })
            }}
          >
            <KeyRound className="h-4 w-4" />
            Reset
          </Button>
        </div>
      ),
    },
  ]

  const searchDirty = draftSearch !== appliedSearch
  const searchCanReset = draftSearch !== '' || appliedSearch !== ''

  if (!canManageUsers(accessOf(actor))) {
    return (
      <PageFrame title="Users">
        <NoAccess />
      </PageFrame>
    )
  }

  function errMessage(e: unknown): string {
    return e instanceof ApiError ? e.message : 'Request failed'
  }

  function openCreate() {
    setError(null)
    setCreateOpen(true)
    setEditUser(null)
  }

  function openEdit(user: BackofficeUser) {
    setError(null)
    setCreateOpen(false)
    setEditUser(user)
    setEditRole(user.role || 'dealing_settlement')
    setEditEnabled(user.enabled)
  }

  function closeDialog() {
    setCreateOpen(false)
    setEditUser(null)
  }

  return (
    <PageFrame
      title="Users"
      description="Staff who can sign in, and the role each person has."
      actions={
        <Button onClick={openCreate}>
          <UserPlus className="h-4 w-4" />
          Add user
        </Button>
      }
    >
      {revealedPassword ? (
        <div role="status" className="rounded-lg bg-[var(--tone-info-bg)] px-4 py-3 text-sm text-[var(--tone-info-fg)]">
          <p className="font-semibold">Temporary password (shown once)</p>
          <p className="mt-1 font-mono break-all">{revealedPassword}</p>
          <p className="mt-1">User must change it on next sign-in.</p>
          <Button variant="ghost" size="sm" className="mt-2" onClick={() => setRevealedPassword(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      {/* Reset-password errors land here; create and edit errors show inside the dialog. */}
      {error && !dialogOpen ? (
        <p className="text-sm font-semibold text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <FilterBarForm
        className={FILTER_BAR}
        onSubmit={() => setAppliedSearch(draftSearch)}
      >
        <div className="flex max-lg:flex-col max-lg:gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:gap-3">
          <Field label="Search" className="max-w-md min-w-48 flex-1">
            <Input
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              placeholder="Email, role, username"
            />
          </Field>
          <FilterBarApply
            isDirty={searchDirty}
            canReset={searchCanReset}
            onReset={() => {
              setDraftSearch('')
              setAppliedSearch('')
            }}
          />
        </div>
      </FilterBarForm>

      <DeskTable
        columns={userColumns}
        rows={filtered}
        rowKey={(u) => u.username}
        loading={usersQuery.isLoading}
        error={usersQuery.error}
        emptyTitle="No users"
        emptyDescription={usersQuery.isError ? (usersQuery.error as Error).message : 'No users match this search.'}
      />

      <Dialog open={dialogOpen} onClose={closeDialog} label={createOpen ? 'Add user' : 'Edit user'}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            if (createOpen) {
              createMut.mutate(undefined, { onError: (err) => setError(errMessage(err)) })
            } else if (editUser) {
              updateMut.mutate(undefined, { onError: (err) => setError(errMessage(err)) })
            }
          }}
        >
          <h2 className="text-lg font-semibold">{createOpen ? 'Add user' : 'Edit user'}</h2>
          {createOpen ? (
            <Field label="Email">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="off"
              />
            </Field>
          ) : (
            <p className="text-sm text-muted-foreground">{editUser?.email}</p>
          )}
          <Field label="Role">
            <GlassSelect
              value={createOpen ? role : editRole}
              onChange={(next) => (createOpen ? setRole(next) : setEditRole(next))}
              options={ROLES.map((r) => ({ value: r, label: roleLabel(r) }))}
            />
          </Field>
          {!createOpen ? (
            <label className="flex min-h-11 items-center gap-2.5 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={editEnabled}
                onChange={(e) => setEditEnabled(e.target.checked)}
              />
              Enabled
            </label>
          ) : null}
          {error ? (
            <p role="alert" className="rounded-md bg-[var(--tone-danger-bg)] px-3 py-2 text-sm font-semibold text-[var(--tone-danger-fg)]">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-4 pt-1">
            <Button type="button" variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMut.isPending || updateMut.isPending}>
              {createOpen ? 'Create' : 'Save'}
            </Button>
          </div>
        </form>
      </Dialog>
    </PageFrame>
  )
}
